import { useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { Droplets, BarChart3, TrendingUp, Layers } from 'lucide-react';

// Define the data types
interface ConsumptionData {
  period: string;
  consumption: number;
  average_consumption: number;
  reading_count?: number;
  meter_count?: number;
}

interface FormattedConsumptionData extends ConsumptionData {
  period_display: string;
}

interface ConsumptionChartProps {
  data?: ConsumptionData[];
  is_loading?: boolean;
}

const ConsumptionChart = ({ data = [], is_loading = false }: ConsumptionChartProps) => {
  const [chart_type, set_chart_type] = useState<'area' | 'line' | 'bar'>('bar');

  const format_consumption = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  const format_tooltip_consumption = (value: number): string => {
    return `${Math.abs(value)?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} units`;
  };

  const format_period = (period: string): string => {
    // Format monthly period (YYYY-MM) for display
    if (period?.length === 7) {
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    // Fallback for other formats
    const date = new Date(period);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const custom_tooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-3 border-b pb-2">
            {format_period(label as string)}
          </p>
          <div className="space-y-2">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center justify-between gap-6 text-sm">
                <span className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-gray-600">{entry.name}:</span>
                </span>
                <span className="font-semibold" style={{ color: entry.color }}>
                  {format_tooltip_consumption(entry.value as number)}
                </span>
              </div>
            ))}
          </div>
          {/* Additional info */}
          <div className="mt-3 pt-3 border-t space-y-1 text-xs text-gray-500">
            <div className="flex justify-between">
              <span>Meter Readings:</span>
              <span>{(payload[0]?.payload as ConsumptionData)?.reading_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Meters Read:</span>
              <span>{(payload[0]?.payload as ConsumptionData)?.meter_count || 0}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate summary stats - handle negative values
  const total_consumption = data?.reduce((sum, item) => sum + Math.abs(item.consumption || 0), 0) || 0;
  const avg_consumption = data?.length > 0 ? total_consumption / data?.length : 0;
  const highest_consumption = data?.length > 0 
    ? Math.max(...data.map(item => Math.abs(item.consumption || 0))) 
    : 0;
  const total_readings = data?.reduce((sum, item) => sum + (item.reading_count || 0), 0) || 0;

  // Calculate growth - using absolute values
  const growth_percentage = data?.length >= 2
    ? Math.round(((Math.abs(data[data?.length - 1].consumption) - Math.abs(data[0].consumption)) / Math.abs(data[0].consumption || 1)) * 100)
    : 0;

  // Format data for display
  const formatted_data: FormattedConsumptionData[] = data?.map(item => ({
    ...item,
    consumption: Math.abs(item.consumption || 0),
    average_consumption: Math.abs(item.average_consumption || 0),
    period_display: format_period(item.period)
  })) || [];

  if (is_loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (data?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Water Consumption Trends</h3>
            <p className="text-sm text-gray-500 mt-1">Track water usage patterns over time</p>
          </div>
        </div>
        <div className="h-80 flex flex-col items-center justify-center">
          <Droplets size={48} className="text-gray-400 mb-4" />
          <p className="text-gray-600 font-medium">No consumption data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Data will appear as meter readings are recorded
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Monthly Water Consumption</h3>
          <p className="text-sm text-gray-500 mt-1">
            Tracking {data?.length} month{data?.length !== 1 ? 's' : ''} • {total_readings} readings
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => set_chart_type('area')}
            className={`p-2 rounded-lg transition-all ${
              chart_type === 'area'
                ? 'bg-forest-green text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Area Chart"
          >
            <Layers size={18} />
          </button>
          <button
            onClick={() => set_chart_type('line')}
            className={`p-2 rounded-lg transition-all ${
              chart_type === 'line'
                ? 'bg-forest-green text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Line Chart"
          >
            <TrendingUp size={18} />
          </button>
          <button
            onClick={() => set_chart_type('bar')}
            className={`p-2 rounded-lg transition-all ${
              chart_type === 'bar'
                ? 'bg-forest-green text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Bar Chart"
          >
            <BarChart3 size={18} />
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gradient-to-br from-forest-green/10 to-forest-green/20 rounded-lg border border-forest-green/30">
            <p className="text-xs text-forest-green font-medium mb-1 uppercase tracking-wide">Total Consumption</p>
            <p className="text-2xl font-bold text-forest-green">
              {format_consumption(total_consumption)}
            </p>
            <p className="text-xs text-dark-forest mt-1">
              {format_tooltip_consumption(total_consumption)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-lime-green/10 to-lime-green/20 rounded-lg border border-lime-green/30">
            <p className="text-xs text-forest-green font-medium mb-1 uppercase tracking-wide">Avg per Month</p>
            <p className="text-2xl font-bold text-forest-green">
              {format_consumption(avg_consumption)}
            </p>
            <p className="text-xs text-dark-forest mt-1">
              {format_tooltip_consumption(avg_consumption)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-success/10 to-success/20 rounded-lg border border-success/30">
            <p className="text-xs text-success font-medium mb-1 uppercase tracking-wide">Highest Month</p>
            <p className="text-2xl font-bold text-success">
              {format_consumption(highest_consumption)}
            </p>
            <p className="text-xs text-green-700 mt-1">
              {format_tooltip_consumption(highest_consumption)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-warning/10 to-warning/20 rounded-lg border border-warning/30">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs text-warning font-medium mb-1 uppercase tracking-wide">Monthly Growth</p>
                <p className={`text-2xl font-bold ${
                  growth_percentage >= 0 ? 'text-warning' : 'text-success'
                }`}>
                  {growth_percentage >= 0 ? '+' : ''}{growth_percentage}%
                </p>
                <p className="text-xs text-orange-700 mt-1">
                  vs. first month
                </p>
              </div>
              <div className={`p-2 rounded-lg ${
                growth_percentage >= 0 ? 'bg-warning/20' : 'bg-success/20'
              }`}>
                <TrendingUp 
                  size={20} 
                  className={growth_percentage >= 0 ? 'text-warning' : 'text-success rotate-180'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            {chart_type === 'area' ? (
              <AreaChart data={formatted_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorConsumption" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1a3d2e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#1a3d2e" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorAverage" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7fd957" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#7fd957" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period_display" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={format_consumption}
                  label={{ value: 'units', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                />
                <Tooltip content={custom_tooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: '14px' }}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="consumption"
                  name="Actual Consumption"
                  stroke="#1a3d2e"
                  strokeWidth={2}
                  fill="url(#colorConsumption)"
                />
                <Area
                  type="monotone"
                  dataKey="average_consumption"
                  name="Average Consumption"
                  stroke="#7fd957"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  fill="url(#colorAverage)"
                />
              </AreaChart>
            ) : chart_type === 'line' ? (
              <LineChart data={formatted_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period_display" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={format_consumption}
                  label={{ value: 'units', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                />
                <Tooltip content={custom_tooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: '14px' }}
                  iconType="circle"
                />
                <Line
                  type="monotone"
                  dataKey="consumption"
                  name="Actual Consumption"
                  stroke="#1a3d2e"
                  strokeWidth={3}
                  dot={{ fill: '#1a3d2e', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  type="monotone"
                  dataKey="average_consumption"
                  name="Average Consumption"
                  stroke="#7fd957"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={{ fill: '#7fd957', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            ) : (
              <BarChart data={formatted_data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period_display" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={format_consumption}
                  label={{ value: 'units', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                />
                <Tooltip content={custom_tooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: '14px' }}
                  iconType="square"
                />
                <Bar 
                  dataKey="consumption" 
                  name="Actual Consumption" 
                  fill="#1a3d2e" 
                  radius={[8, 8, 0, 0]} 
                />
                <Bar 
                  dataKey="average_consumption" 
                  name="Average Consumption" 
                  fill="#7fd957" 
                  radius={[8, 8, 0, 0]} 
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Legend Info */}
        <div className="flex flex-wrap gap-6 text-sm bg-off-white rounded-lg p-4 border border-light-gray">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-forest-green"></div>
            <span className="text-charcoal">
              <span className="font-semibold">Actual Consumption</span> - Total monthly water usage in units
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-lime-green"></div>
            <span className="text-charcoal">
              <span className="font-semibold">Average Consumption</span> - Average units per reading
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ConsumptionChart };